import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Pin, Loader2 } from 'lucide-react';
import { useLocationStreaming } from '@/hooks/useLocationStreaming';
import { validateLocation } from '@/lib/validation';

const StreamingLocationInput = ({ onLocationSelect, theme, autoFillFromGeolocation = true, initialValue = '', placeholder: placeholderOverride }) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  // When initialValue is provided (URL hydration), the field is already filled
  // and shouldn't be overwritten by the streaming geolocation flow.
  const [isManualInput, setIsManualInput] = useState(!!initialValue);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);

  const {
    displayText,
    finalLocation,
    coordinates,
    isLoading,
    isStreaming,
    error,
    fetchLocation,
    resetLocation
  } = useLocationStreaming();

  // Auto-fetch location on mount — opt-out for trip stops 2+ where geolocation
  // would silently overwrite the user's intended destination.
  useEffect(() => {
    if (autoFillFromGeolocation) fetchLocation();
  }, [fetchLocation, autoFillFromGeolocation]);

  // Handle location selection when streaming completes (only when auto-fill is on)
  useEffect(() => {
    if (!autoFillFromGeolocation) return;
    if (finalLocation && coordinates && !isStreaming && !isManualInput) {
      const timeoutId = setTimeout(() => {
        onLocationSelect({
          name: finalLocation,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        });
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [finalLocation, coordinates, isStreaming, isManualInput, onLocationSelect, autoFillFromGeolocation]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = async (input) => {
    if (input.length < 2) {
      setSuggestions([]);
      return;
    }

    // Cancel any in-flight request — keystrokes typed mid-request are stale.
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoadingSuggestions(true);
    try {
      let results = [];

      // Handle both "berlin ohio" and "berlin,ohio" formats
      const hasComma = input.includes(',');
      const hasSpace = input.includes(' ') && !hasComma;

      if (hasComma || hasSpace) {
        // Normalize the input - replace space with comma for consistent processing
        const normalizedInput = hasSpace ? input.replace(/\s+/g, ', ') : input;
        const parts = normalizedInput.split(',').map(p => p.trim());
        const city = parts[0];
        const stateOrCountry = parts[1];

        const fullResponse = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(normalizedInput)}&count=10&language=en&format=json`,
          { signal: controller.signal }
        );
        const fullData = await fullResponse.json();
        results = fullData.results || [];

        if (results.length === 0 && stateOrCountry) {
          const cityResponse = await fetch(
            `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(city)}&count=50&language=en&format=json`,
            { signal: controller.signal }
          );
          const cityData = await cityResponse.json();
          const cityResults = cityData.results || [];

          results = cityResults.filter(result => {
            const admin1Match = result.admin1?.toLowerCase().includes(stateOrCountry.toLowerCase());
            const countryMatch = result.country?.toLowerCase().includes(stateOrCountry.toLowerCase());
            return admin1Match || countryMatch;
          });
        }
      } else {
        const encodedInput = encodeURIComponent(input);
        const response = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodedInput}&count=15&language=en&format=json`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch suggestions');
        }

        const data = await response.json();
        results = data.results || [];
      }

      setSuggestions(results);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Error fetching suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // 250ms debounce — Emil rule: don't fire an API call on every keystroke.
  const debouncedFetchSuggestions = (input) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(input), 250);
  };

  useEffect(() => () => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    if (abortControllerRef.current) abortControllerRef.current.abort();
  }, []);

  // Try direct geocoding for specific formats like "Berlin, Ohio"
  const tryDirectGeocode = async (input) => {
    try {
      const response = await fetch(`https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(input)}&count=50&language=en&format=json`);
      const data = await response.json();
      
      if (data.results && data.results.length > 0) {
        // Find best match for the input
        const exactMatch = data.results.find(result => 
          result.name.toLowerCase().includes(input.split(',')[0].trim().toLowerCase())
        );
        return exactMatch || data.results[0];
      }
      return null;
    } catch (error) {
      console.error('Direct geocode failed:', error);
      return null;
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    setIsManualInput(true);
    
    // Only validate and fetch suggestions if value is not empty
    if (value.trim().length === 0) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    try {
      const sanitizedValue = validateLocation(value);
      debouncedFetchSuggestions(sanitizedValue);
      setShowSuggestions(true);
    } catch (error) {
      // Don't log validation errors for empty strings or during normal typing
      if (value.trim().length > 0) {
        console.error('Input validation error:', error);
      }
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    // Format the display name based on location type
    const isUSState = suggestion.country_code === 'US' && suggestion.admin1;
    const isIndianState = suggestion.country_code === 'IN' && suggestion.admin1;
    
    let locationName;
    if (isUSState || isIndianState) {
      // For US and India: City, State
      locationName = `${suggestion.name}${suggestion.admin1 ? `, ${suggestion.admin1}` : ''}`;
    } else {
      // For other countries: City, Country
      locationName = `${suggestion.name}${suggestion.country ? `, ${suggestion.country}` : ''}`;
    }
    
    setInputValue(locationName);
    setShowSuggestions(false);
    onLocationSelect({
      ...suggestion,
      name: locationName // Update the name to include the formatted location
    });
  };

  const formatLocationDetails = (suggestion) => {
    const details = [];
    if (suggestion.admin1) details.push(suggestion.admin1);
    if (suggestion.country) details.push(suggestion.country);
    return [...new Set(details)].join(', ');
  };

  const handleInputFocus = () => {
    setIsManualInput(true);
    if (inputValue && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const getPlaceholderText = () => {
    if (isLoading) return 'Finding your location...';
    if (isStreaming || displayText) return displayText;
    return placeholderOverride || 'Where are you?';
  };

  const shouldShowLocationIcon = () => {
    return !error && (displayText || finalLocation);
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="relative">
        <Input
          ref={inputRef}
          type="text"
          value={isManualInput ? inputValue : (displayText || '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          placeholder={getPlaceholderText()}
          className="w-full pr-10"
          autoComplete="off"
        />
        
        {/* Location Icon (removed loading icon to prevent flash) */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {shouldShowLocationIcon() ? (
            <Pin className="h-4 w-4 text-muted-foreground" />
          ) : null}
        </div>
      </div>

      {/* Suggestions Dropdown — glass surface matching the rest of the form chrome,
          z-30 so it sits above page chrome but below Radix-portaled popovers (z-50). */}
      {showSuggestions && suggestions.length > 0 && (
        <ul className="autocomplete-dropdown absolute z-30 w-full mt-1 max-h-60 overflow-auto rounded-md border border-foreground/15 shadow-lg bg-background/85 backdrop-blur-md text-foreground">
          {suggestions.map((suggestion) => (
            <li
              key={`${suggestion.latitude}-${suggestion.longitude}`}
              onClick={() => handleSuggestionClick(suggestion)}
              className="px-4 py-2 cursor-pointer hover:bg-foreground/10 transition-colors duration-150"
            >
              <div className="font-medium">{suggestion.name}</div>
              {formatLocationDetails(suggestion) && (
                <div className="text-sm text-muted-foreground">
                  {formatLocationDetails(suggestion)}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

    </div>
  );
};

export default StreamingLocationInput;